/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ChartItemData, ChartSeriesData, ValueFormats, Group } from '../UNSAFE_visTypes/chart';
import { VisTabularDatatip } from '../../PRIVATE_VisTabularDatatip';
import { TableRowData } from '../../PRIVATE_VisTabularDatatip';

/**
 * Returns the data required for the VisTabularDatatip to render.
 * @param activeInfo active item info.
 * @param series The array of chart series.
 * @param groups The array of chart groups.
 * @param valueFormats The array of chart series.
 * @param getDataItem function which returns chartItem
 */
export function getChartTabularData<K, D extends ChartItemData<K>>(
  data: D,
  seriesValue: string,
  groupValue: string,
  valueFormats?: ValueFormats,
  isTimeAxis?: boolean
) {
  //TODO: Use translation strings
  const value = data.value;
  const itemValue = valueFormats?.value?.format ? valueFormats?.value?.format?.(value) : value;
  const tableData: TableRowData[] = [];

  if (valueFormats?.series?.isDisplayed != false) {
    tableData.push({
      key: valueFormats?.series?.label || 'Series',
      value: seriesValue
    });
  }
  if (valueFormats?.group?.isDisplayed != false) {
    tableData.push({
      key: valueFormats?.group?.label || (isTimeAxis ? 'Date' : 'Group'),
      value: groupValue
    });
  }
  if (valueFormats?.value?.isDisplayed != false) {
    tableData.push({ key: valueFormats?.value?.label || 'Value', value: itemValue });
  }
  return tableData;
}

export function getDefaultDatatipRenderer<
  K extends string | number,
  D extends ChartItemData<K>
>(options: {
  series: ChartSeriesData<K, D>;
  groups: Group;
  valueFormats?: ValueFormats;
  timeAxisType?: 'mixedFrequency' | 'enabled' | 'skipGaps';
}) {
  const isMixedFrequency = options.timeAxisType === 'mixedFrequency';
  const isRegularTimeAxis =
    options.timeAxisType === 'enabled' || options.timeAxisType === 'skipGaps';

  return (context: { data: D }) => {
    const groupValue = options.groups.name || options.groups.id;
    const timeValue = isMixedFrequency
      ? context.data.x
      : isRegularTimeAxis
      ? groupValue
      : undefined;
    return {
      content: (
        <VisTabularDatatip
          tableData={getChartTabularData<K, D>(
            context.data,
            options.series.name || options.series.id.toString(),
            timeValue || options.groups.name || options.groups.id,
            options.valueFormats,
            options.timeAxisType != null
          )}
        />
      ),
      borderColor: context.data.color || options.series.color
    };
  };
}
